import React from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
export interface Testimonial {
  initials: string;
  name: string;
  location: string;
  text: string;
  color: string;
  pack?: string;
}
interface TestimonialsProps {
  testimonials: Testimonial[];
  title?: string;
  className?: string;
}
export function Testimonials({
  testimonials,
  title,
  className = ''
}: TestimonialsProps) {
  return <section className={`py-16 bg-[#0f1d35] ${className}`} data-id="element-338">
      <div className="container mx-auto px-4" data-id="element-339">
        {title && <h2 className="font-bold text-center mb-12 text-white text-[16px] md:text-2xl" data-id="element-340">
            {title}
          </h2>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-341">
          {testimonials.map((t, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} className="bg-[#0a1428]/70 p-6 rounded-2xl shadow-lg border border-slate-500/30 flex flex-col h-full" data-id="element-342">
              <div className="flex items-center gap-1 mb-4" data-id="element-343">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-slate-300 fill-slate-300" data-id="element-344" />)}
              </div>

              <p className="text-slate-300 italic mb-6 flex-grow" data-id="element-345">"{t.text}"</p>

              <div className="flex items-center gap-3 mt-auto pt-4 border-t border-slate-500/30" data-id="element-346">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${t.color}`} data-id="element-347">
                  {t.initials}
                </div>
                <div data-id="element-348">
                  <p className="font-bold text-white text-sm" data-id="element-349">{t.name}</p>
                  <p className="text-xs text-slate-400" data-id="element-350">{t.location}</p>
                  {t.pack && <p className="text-xs font-medium text-slate-300/90 mt-0.5" data-id="element-351">
                      {t.pack}
                    </p>}
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
export const initialTestimonials: Testimonial[] = [{
  initials: 'MR',
  name: 'María R.',
  location: 'Docente de Geografía · Corrientes',
  text: 'Llevaba años buscando material argentino de hidrología y siempre encontraba lo mismo: textos genéricos, sin ejemplos locales, sin actividades listas. Abrí el Tomo 2 y en una tarde tuve todo el material para tres semanas de clases sobre cuencas. El Paraná, el Bermejo, la Cuenca del Plata. Todo contextualizado.',
  color: 'bg-blue-700'
}, {
  initials: 'JC',
  name: 'Julián C.',
  location: 'Maestro de Ciencias Naturales · Mendoza',
  text: 'El Bonus 1 con 100 actividades me salvó el trimestre. Crucigrama de cuencas, sopas de letras, casos de análisis, experimentos. Lo usé en primaria y en el ciclo básico de secundaria. Mis colegas me piden que les pase el material.',
  color: 'bg-slate-600'
}, {
  initials: 'SP',
  name: 'Sofía P.',
  location: 'Docente de Educación Ambiental · Córdoba',
  text: 'El Tomo 5 sobre Calidad del Agua y el Tomo 3 sobre Acuíferos me dieron lo que nunca encontré en ningún libro de texto: el caso del Riachuelo, el Acuífero Guaraní, el arsénico en el agua del interior. Mis alumnos no lo podían creer. Preguntaban todo el tiempo.',
  color: 'bg-blue-900'
}];
export const moreTestimonials: Testimonial[] = [{
  initials: 'PM',
  name: 'Pablo M.',
  location: '🏙️ Buenos Aires',
  text: 'Dos años enseñando hidrología con fotocopias sin fuente y textos sin datos argentinos. Con el Megapack en una tarde tenía el contenido de todo el año organizado, con casos reales y actividades listas.',
  color: 'bg-blue-800',
  pack: 'Megapack Hidrología'
}, {
  initials: 'RL',
  name: 'Rosa L.',
  location: '🏙️ Santa Fe',
  text: 'El Tomo 4 sobre fenómenos extremos me lo pidieron prestado tres colegas del departamento. La bajante del Paraná, los incendios del Delta, el aluvión de Tartagal. Mis alumnos no podían creer que todo eso estaba explicado con datos reales.',
  color: 'bg-slate-700',
  pack: 'Megapack Hidrología'
}, {
  initials: 'GF',
  name: 'Gabriela F.',
  location: '🏙️ Tucumán',
  text: 'Con las 100 actividades del Bono 1 armé el año completo de actividades en una tarde del sábado. Crucigrama de cuencas, casos del Riachuelo, experimento de infiltración. Todo con la respuesta incluida.',
  color: 'bg-slate-600',
  pack: 'Megapack + Nivel Avanzado'
}, {
  initials: 'AM',
  name: 'Andrés M.',
  location: '🏙️ Rosario',
  text: 'Las planificaciones del Bono 4 me ahorran horas cada trimestre. Están alineadas con los NAP, tienen los objetivos por nivel y las secuencias didácticas clase por clase. Nunca más empiezo de cero.',
  color: 'bg-blue-700',
  pack: 'Megapack + Nivel Avanzado'
}, {
  initials: 'SV',
  name: 'Silvia V.',
  location: '🏙️ Córdoba',
  text: 'El Atlas Hidrológico del Bono 3 lo proyecto en cada clase. Mapas de cuencas, acuíferos, represas y problemas hídricos de Argentina. Mis alumnos antes no ubicaban el Paraná en el mapa. Ahora me explican la diferencia entre el curso alto y el Delta.',
  color: 'bg-slate-800',
  pack: 'Megapack Hidrología'
}];